import { ComponentProps, useState } from "react";
import { format, isValid, parse } from "date-fns";

import { ClockPicker } from "@mui/x-date-pickers/ClockPicker";
import { MobileTimePicker } from "@mui/x-date-pickers/MobileTimePicker";

import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

import useToggle from "libs/hooks/useToggle";

type CustomTimePickerProps = {
  value?: Date | null;
  onChange?: (value: Date | null) => void;
  label?: string;
  name?: string;
  required?: boolean;
  disabled?: boolean;
  minTime?: Date;
  maxTime?: Date;
};

export function CustomTimePicker({ value, onChange, label = "時間", name, required, disabled, minTime, maxTime }: CustomTimePickerProps) {
  const [open, toggle] = useToggle(false);

  const [inputValue, setInputValue] = useState<string>(value && isValid(value) ? format(value, "HH:mm") : "");
  const [clockValue, setClockValue] = useState<Date | null>(value ?? null);

  const displayValue = value && isValid(value) ? format(value, "HH:mm") : "";

  const handleOpen = () => {
    if (disabled) return;
    setInputValue(value && isValid(value) ? format(value, "HH:mm") : "");
    setClockValue(value ?? null);
    toggle.toTrue();
  };

  const isValidInput = (parsed: Date, raw: string): boolean => {
    if (!isValid(parsed)) return false;
    if (raw.length !== 5) return false;
    const parsedMinutes = parsed.getHours() * 60 + parsed.getMinutes();
    if (minTime) {
      const minMinutes = minTime.getHours() * 60 + minTime.getMinutes();
      if (parsedMinutes < minMinutes) return false;
    }
    if (maxTime) {
      const maxMinutes = maxTime.getHours() * 60 + maxTime.getMinutes();
      if (parsedMinutes > maxMinutes) return false;
    }
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputValue(raw);
    const parsed = parse(raw, "HH:mm", new Date());
    if (isValidInput(parsed, raw)) {
      setClockValue(parsed);
    }
  };

  const handleClockChange = (newDate: Date | null) => {
    setClockValue(newDate);
    if (newDate && isValid(newDate)) {
      setInputValue(format(newDate, "HH:mm"));
    }
  };

  const handleConfirm = () => {
    onChange?.(clockValue);
    toggle.toFalse();
  };

  const handleCancel = () => {
    toggle.toFalse();
  };

  return (
    <>
      <TextField
        label={label}
        value={displayValue}
        required={required}
        disabled={disabled}
        name={name}
        inputProps={{ readOnly: true }}
        onClick={handleOpen}
        sx={{ cursor: disabled ? "default" : "pointer" }}
      />

      <Dialog open={open} onClose={handleCancel}>
        <DialogContent>
          <Stack spacing={2} alignItems="center">
            <TextField
              label={label}
              value={inputValue}
              onChange={handleInputChange}
              placeholder="HH:mm"
              inputProps={{ pattern: "[0-9]{2}:[0-9]{2}" }}
              autoFocus
            />
            <ClockPicker date={clockValue} onChange={handleClockChange} ampm={false} minTime={minTime} maxTime={maxTime} />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleConfirm} disabled={!isValidInput(parse(inputValue, "HH:mm", new Date()), inputValue)}>
            確認
          </Button>
          <Button color="error" onClick={handleCancel}>
            取消
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default function TimePicker(props: ComponentProps<typeof MobileTimePicker>) {
  return <MobileTimePicker ampm={false} {...props} />;
}
